import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 환율(미국·일본·중국·유럽)과 자재 단가.
 *
 * 서버가 진실이다(`GET /api/v1/home/market`). 수출입은행 인증키가 번들에 실리면 안 되고
 * ({@link useWeather} 와 같은 규칙), 자재 단가는 애초에 우리 DB 에 있다.
 *
 * **두 칸의 성격이 다르다.** 환율은 제공자가 고시한 값이고, 자재 단가는 담당자가 넣은 값이다.
 * 그래서 기준일을 따로 들고 다니고, 위젯도 두 칸을 따로 그린다 — 비면 그 칸은 아예 없다
 * (등록하지 않은 회사에게 매일 「미등록」을 보여 주지 않기 위해서다).
 *
 * ## 이 요청은 바깥으로 나가지 않는다 (사용자 지시 2026-09-02)
 *
 * 환율도 이제 우리 표에서 온다(`ref_exchange_rates`). 수출입은행을 부르는 일은 서버의 수집기가
 * 따로 하고, 이 요청은 그 표를 읽을 뿐이라 **제공자가 조용해도 늦지도 실패하지도 않는다.**
 * 그래서 응답이 비는 경우는 「아직 안 채워졌다」 하나뿐이고, 위젯은 그때 오류가 아니라
 * 「불러오는 중입니다」 로 둔다 — 사유는 관리자 화면에서만 말한다(환경설정 › 외부 API 설정).
 *
 * ## 30분마다 다시 묻는다 — 환율 때문이다 (사용자 지시 2026-08-27)
 *
 * 홈을 띄워 둔 채 아침을 보내는 사람이 있다. 고시가 나오는 시각(영업일 11시경)에 화면을 열어 두고
 * 있었다면, 다시 부르지 않는 한 그 사람의 환율은 하루 종일 전일 값이다. 서버 수집기가 그 시각
 * 직후에 표를 채우므로, 이 주기가 그것을 30분 안에 주워 온다.
 *
 * **자재 단가는 이 주기의 대상이 아니다.** 타 부서가 직접 넣는 표라 사람이 저장하는 순간이 곧
 * 갱신이다. 같은 응답에 실려 함께 새로 올 뿐이며 — 그 편이 낫다.
 */

export type MarketRate = {
  /** KES · LAK · UZS 는 수출입은행 밖 보조 통로(OpenExchangeRateClient)가 채운다. */
  currency: 'USD' | 'JPY' | 'CNY' | 'EUR' | 'KES' | 'LAK' | 'UZS';
  /** 「미국 USD」 — 코드만 있으면 CNY 가 무엇인지 아무도 모른다. */
  label: string;
  /** 몇 단위당 원화인가. 엔화만 100 이다(국내 고시가 100엔 기준). */
  unit: number;
  rate: number;
  /** 직전 고시일 대비. 직전을 못 찾았으면 null 이고, 화면은 등락을 감춘다. */
  change: number | null;
  /**
   * 최근 고시일들의 값(**오래된 순**). 숫자 아래 스파크라인으로 그린다.
   *
   * 점이 하나뿐이면(수집 첫날) 그리지 않는다 — 점 하나는 추이가 아니다. 단위가 지금과 다른 날은
   * 서버가 이미 빼고 보낸다(100엔 → 1엔 표기 변경).
   */
  trend: number[];
};

export type MarketMaterial = {
  name: string;
  spec: string | null;
  unit: string;
  unitPrice: number;
  /** 이 단가의 기준일. 숫자 옆에 늘 함께 쓴다 — 없으면 오래됐는지 알 길이 없다. */
  quotedOn: string;
  change: number | null;
  sourceNote: string | null;
};

/** 전국 평균 유가 하나(오피넷). 값은 원/L. */
export type MarketFuel = {
  product: 'GASOLINE' | 'DIESEL' | 'LPG';
  label: string;
  price: number;
  /** 전일 대비. 없으면 null 이고, 화면은 등락을 감춘다. */
  change: number | null;
  /** 최근 기준일들의 값(**오래된 순**). */
  trend: number[];
};

export type MarketSnapshot = {
  /** 수집이 아직이면 빈 목록이다. 그때 위젯은 이 칸을 그리지 않는다 — 자재 단가와 같은 규칙. */
  rates: MarketRate[];
  /** 오늘이 아닐 수 있다 — 주말·공휴일·11시 이전이면 마지막 영업일 고시다. */
  ratesQuotedOn: string | null;
  materials: MarketMaterial[];
  /** 재시작 전 서버는 이 칸을 보내지 않는다 — 없으면 빈 목록으로 읽는다. */
  fuels?: MarketFuel[];
  fuelsOn?: string | null;
  /** 유가를 받아 온 시각(ISO). 환율 고시일과 따로 — 유가는 하루에도 몇 번 바뀐다. */
  fuelsMeasuredAt?: string | null;
  /** 차량별 연비 — 회사가 정한 기준값. 재시작 전 서버는 보내지 않는다. */
  fuelEfficiencies?: MarketFuelEfficiency[];
  /** 지금 사람이 연비를 고칠 수 있는가(인사팀·최고관리자). 연필을 이것으로 세운다. */
  canEditFuelEfficiency?: boolean;
};

export type MarketFuelEfficiency = {
  product: MarketFuel['product'];
  label: string;
  kmPerL: number;
};

/**
 * 홈을 오가는 동안에는 다시 부르지 않는다. 주기 갱신은 아래 {@link REFETCH_INTERVAL_MS} 가 맡는다 —
 * 둘을 같은 값으로 두면 "화면을 다시 열었다" 와 "주기가 돌았다" 가 겹쳐 요청이 두 번 나간다.
 */
const STALE_TIME_MS = 30 * 60 * 1000;

/**
 * 30분마다 다시 묻는다. **탭이 뒤에 있으면 돌지 않는다**(react-query 기본) — 아무도 안 보는 화면
 * 때문에 수출입은행 일일 한도를 쓸 이유가 없다. 돌아오면 그때 한 번 부른다.
 */
const REFETCH_INTERVAL_MS = 30 * 60 * 1000;

const MARKET_PATH = '/v1/home/market';

export function useMarket(): UseQueryResult<MarketSnapshot> {
  const { data: session } = useSession();

  return useQuery({
    queryKey: queryKeys.market,
    queryFn: () => apiFetch<MarketSnapshot>(MARKET_PATH),
    enabled: !!session?.user?.id,
    staleTime: STALE_TIME_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}
